import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Animation } from '../../animation/animation';
import { QKey, useAnswerContext } from '../../../context';
import { BaseInsight } from './base-insight';
import { BaseQuestion, IBaseQuestion } from './base-question';
import Lottie from 'lottie-react';
import { animationData } from '../../../lottie';
import { submitAssessment } from '../../../api';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
  },
  image: {
    position: 'absolute',
    [theme.breakpoints.down('sm')]: {
      borderRadius: 15,
      width: 200,
      height: 200,
      bottom: 50,
      margin: '20px 0',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      width: 200,
      right: -115,
      top: -5,
      zIndex: 10,
    },
    [theme.breakpoints.up('md')]: {
      width: 200,
      right: -50,
      top: 50,
      zIndex: 10,
    },
  },
  imageBackground: {
    width: 220,
    height: 240,
    zIndex: 1,
    [theme.breakpoints.down('sm')]: {
      borderRadius: 15,
      width: 200,
      height: 200,
      margin: '20px 0',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      position: 'absolute',
      borderRadius: 20,
      right: -65,
      top: -10,
      transition: 'transform 0.7s cubic-bezier(0.445, 0.050, 0.550, 0.950) 0s',
    },
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
      borderRadius: 20,
      right: -60,
      top: 54,
      transition: 'transform 0.7s cubic-bezier(0.445, 0.050, 0.550, 0.950) 0s',
    },
  },
}));

export interface QuestionProps {
  type: 'slider' | 'order' | 'select';
  questionKey: QKey;
  headerText: string;
  bg: string;
  help: string;
  insight?: { text: string[] | string; cite: string };
  last?: boolean;
  options?: Array<{ text: string; value: number }>;
  src: string;
  color: string;
  updateScroll: () => void;
}

export const Question: React.FC<IBaseQuestion & QuestionProps> = React.memo(
  (props) => {
    const { onNext, insight, src, last = false, updateScroll, ...rest } = props;
    const show = sessionStorage.getItem('showInsight') === 'true';
    const [showInsight, setShowInsight] = useState(show);
    const [direction, setDirection] = useState<'left' | 'right' | 'none'>(
      'left',
    );
    const [height, setHeight] = useState(70);
    const { values, setComplete } = useAnswerContext();
    const rootRef = useRef<HTMLDivElement>(null);
    const { root, image, imageBackground } = useStyles();

    useEffect(() => {
      sessionStorage.setItem('showInsight', showInsight.toString());
    }, [showInsight]);

    useLayoutEffect(() => {
      if (!rootRef?.current) {
        return;
      }
      const resizeObserver = new ResizeObserver((data) =>
        setHeight((data[0].contentRect.height - 250) / 2),
      );
      resizeObserver.observe(rootRef.current);
      return () => {
        resizeObserver.disconnect();
      };
    }, []);
    const theme = useTheme();
    const matches = !useMediaQuery(theme.breakpoints.up('md'));
    const matchesSM = !useMediaQuery(theme.breakpoints.up('sm'));

    const moveX =
      showInsight && insight
        ? matches
          ? -(rootRef.current?.clientWidth ?? 800) + 90
          : -700
        : 0;
    const animatedImage = (
      <Animation animationKey={rest.questionKey} onlyFade delay={0.5}>
        <div>
          <div
            className={imageBackground}
            style={
              matchesSM
                ? { background: rest.bg }
                : {
                    transform: `translate(${
                      moveX + (showInsight && insight ? -10 : 10)
                    }px, ${height - 25}px)`,
                    background: rest.bg,
                  }
            }
          ></div>
          <Lottie
            animationData={animationData[rest.questionKey]}
            className={image}
            style={{
              transition:
                'transform 0.7s cubic-bezier(0.445, 0.050, 0.550, 0.950) 0.035s',
              transform: matchesSM
                ? undefined
                : `translate(${moveX + (matches ? -40 : 0)}px, ${
                    matches ? height - 30 : height - 0
                  }px)`,
            }}
          />
        </div>
      </Animation>
    );

    return (
      <div className={root} ref={rootRef}>
        {!matches ? animatedImage : null}
        {showInsight && insight ? (
          <Animation
            animationKey={showInsight}
            direction={direction}
            insight={true}
          >
            <BaseInsight
              questionKey={rest.questionKey}
              animatedImage={matches ? animatedImage : undefined}
              nextText={last ? 'Complete assessment' : 'Next question'}
              {...insight}
              onNext={() => {
                if (last) {
                  setComplete();
                  submitAssessment(
                    null,
                    {...values},
                  );
                  return;
                }
                onNext();
                setShowInsight(false);
                updateScroll();
              }}
              onBack={() => {
                setShowInsight(false);
                setDirection('right');
                updateScroll();
              }}
            />
          </Animation>
        ) : (
          <Animation
            animationKey={props.questionKey}
            direction={insight ? direction : undefined}
          >
            <BaseQuestion
              {...rest}
              insight={!!insight}
              animatedImage={matches ? animatedImage : undefined}
              onNext={() => {
                if (insight) {
                  setShowInsight(true);
                  updateScroll();
                  setDirection('left');
                  return;
                }
                onNext();
              }}
            />
          </Animation>
        )}
      </div>
    );
  },
);
